import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_PUBLISHED_QUESTIONS_API = process.env.REACT_APP_ADMIN_PUBLISHED_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/published-questions/`;
const ADMIN_TEST_HISTORY_API = process.env.REACT_APP_ADMIN_TEST_HISTORY_API || `${API_BASE_URL}/api/admin-app/test-history/`;

const AdminCreateTestPage = () => {
    const navigate = useNavigate();
    const [topics, setTopics] = useState([]); // [{ name, count }]
    const [quizName, setQuizName] = useState('');
    const [loading, setLoading] = useState(true);
    const [sections, setSections] = useState([
        { topic: '', question_count: 5, time_minutes: 10 }
    ]);
    const [activeSectionIndex, setActiveSectionIndex] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const headers = useMemo(() => {
        // Try guest_access_token first (for backward compatibility)
        let token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        
        // If not found, try admin userDetails (from redux-based login)
        if (!token) {
            const userDetailsStr = localStorage.getItem('userDetails');
            if (userDetailsStr) {
                try {
                    const userDetails = JSON.parse(userDetailsStr);
                    token = userDetails.access || '';
                } catch (e) {
                    // JSON parse error, token remains empty
                }
            }
        }
        
        if (!token) return null;
        return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
    }, []);

    const topicCountMap = useMemo(() => {
        return topics.reduce((acc, topic) => {
            acc[topic.name] = topic.count;
            return acc;
        }, {});
    }, [topics]);

    const getAvailableCount = (topicName) => {
        if (!topicName) return 0;
        return topicCountMap[topicName] || 0;
    };

    const clampQuestionCount = (topicName, value) => {
        const available = getAvailableCount(topicName);
        const parsed = Number(value);

        if (!Number.isFinite(parsed) || parsed < 0) {
            return 0;
        }

        if (!available) {
            return parsed;
        }

        return Math.min(parsed, available);
    };

    const isSectionValid = (section) => {
        if (!section.topic) return false;
        const qc = Number(section.question_count);
        const tm = Number(section.time_minutes);
        if (!Number.isInteger(qc) || qc <= 0) return false;
        if (!Number.isInteger(tm) || tm <= 0) return false;
        const available = getAvailableCount(section.topic);
        return available > 0 && qc <= available;
    };

    const canCreateTest = useMemo(() => {
        if (loading || submitting || !headers) return false;
        return sections.every(isSectionValid);
    }, [headers, loading, sections, submitting, topicCountMap]);

    useEffect(() => {
        const fetchTopics = async () => {
            if (!headers) {
                setTopics([]);
                setLoading(false);
                return;
            }

            try {
                const res = await fetch(ADMIN_PUBLISHED_QUESTIONS_API, { headers });
                const data = res.ok ? await res.json() : [];
                // derive unique topic names
                // group by topic name and count available published questions
                const map = {};
                (Array.isArray(data) ? data : []).forEach((q) => {
                    const name = (q.topic_name || q.topic || 'Uncategorized') + '';
                    map[name] = (map[name] || 0) + 1;
                });
                const arr = Object.keys(map).map((k) => ({ name: k, count: map[k] })).sort((a,b) => a.name.localeCompare(b.name));
                setTopics(arr);
            } catch (e) {
                setTopics([]);
            } finally {
                setLoading(false);
            }
        };
        fetchTopics();
    }, [headers]);

    const updateSection = (index, key, value) => {
        setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [key]: value } : s)));
    };

    const addSection = () => setSections((prev) => [...prev, { topic: '', question_count: 5, time_minutes: 10 }]);
    const removeSection = (i) => setSections((prev) => {
        const next = prev.filter((_, idx) => idx !== i);
        return next.length > 0 ? next : [{ topic: '', question_count: 5, time_minutes: 10 }];
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!headers) {
            setError('You are not authenticated.');
            return;
        }

        // validate
        for (const s of sections) {
            if (!s.topic) { setError('Please select topic for all sections.'); return; }
            const qc = Number(s.question_count);
            const tm = Number(s.time_minutes);
            if (!Number.isInteger(qc) || qc <= 0) { setError('Enter valid question counts.'); return; }
            if (!Number.isInteger(tm) || tm <= 0) { setError('Enter valid time (minutes) for sections.'); return; }
        }

        const payload = { name: quizName.trim(), sections };

        setSubmitting(true);
        try {
            const res = await fetch(ADMIN_TEST_HISTORY_API, {
                method: 'POST', headers, body: JSON.stringify(payload)
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data?.error || data?.detail || 'Failed to create test.');
                return;
            }
            setMessage('Test created successfully.');
            // optionally navigate back to test history
            navigate('/admin/test-history?role=admin');
        } catch (err) {
            setError('Unable to create test right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            <div className="admin-college-header">
                <div>
                    <h3>Create Test</h3>
                    <p className="admin-subtitle">Configure test sections: topic, question count and time (minutes).</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                {error && <div className="admin-security-feedback admin-security-feedback-error">{error}</div>}
                {message && <div className="admin-security-feedback admin-security-feedback-success">{message}</div>}

                <div style={{ marginBottom: '12px', maxWidth: '420px' }}>
                    <label className="admin-security-label">Quiz Name</label>
                    <input
                        className="admin-security-input"
                        type="text"
                        placeholder="Enter quiz name"
                        value={quizName}
                        onChange={(e) => setQuizName(e.target.value)}
                        maxLength={255}
                    />
                </div>
                <div style={{ marginBottom: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                        {sections.map((section, index) => (
                            <button
                                key={index}
                                type="button"
                                onClick={() => setActiveSectionIndex(index)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '10px 14px',
                                    borderRadius: '6px',
                                    border: activeSectionIndex === index ? '1px solid #3b82f6' : '1px solid #d1d5db',
                                    background: activeSectionIndex === index ? '#4f86c6' : '#fff',
                                    color: activeSectionIndex === index ? '#fff' : '#334155',
                                    fontWeight: 700,
                                    boxShadow: activeSectionIndex === index ? '0 2px 6px rgba(59,130,246,0.35)' : 'none'
                                }}
                            >
                                <span>{`Section ${index + 1}`}</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', borderRadius: '50%', background: activeSectionIndex === index ? 'rgba(255,255,255,0.22)' : '#e2e8f0', fontSize: '11px', lineHeight: 1 }}>i</span>
                            </button>
                        ))}
                    </div>

                    {sections[activeSectionIndex] ? (
                        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '14px' }}>
                            <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>
                                Topic: {sections[activeSectionIndex].topic || 'Select a topic'}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 120px 40px', gap: '12px', alignItems: 'end' }}>
                                <div>
                                    <label className="admin-security-label">Topic</label>
                                    <select className="admin-security-input" value={sections[activeSectionIndex].topic} onChange={(e) => updateSection(activeSectionIndex, 'topic', e.target.value)} disabled={loading}>
                                        <option value="">Select topic</option>
                                        {topics.map((t) => <option key={t.name} value={t.name}>{t.name} ({t.count} questions)</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="admin-security-label">Questions</label>
                                    <input
                                        className="admin-security-input"
                                        type="number"
                                        min="0"
                                        max={getAvailableCount(sections[activeSectionIndex].topic) || undefined}
                                        step="1"
                                        value={sections[activeSectionIndex].question_count}
                                        onChange={(e) => updateSection(activeSectionIndex, 'question_count', clampQuestionCount(sections[activeSectionIndex].topic, e.target.value))}
                                        onBlur={(e) => updateSection(activeSectionIndex, 'question_count', clampQuestionCount(sections[activeSectionIndex].topic, e.target.value))}
                                        aria-invalid={sections[activeSectionIndex].topic && Number(sections[activeSectionIndex].question_count) > getAvailableCount(sections[activeSectionIndex].topic)}
                                    />
                                </div>
                                <div>
                                    <label className="admin-security-label">Time (mins)</label>
                                    <input className="admin-security-input" type="number" min="1" value={sections[activeSectionIndex].time_minutes} onChange={(e) => updateSection(activeSectionIndex, 'time_minutes', Number(e.target.value))} />
                                </div>
                                <div>
                                    <button type="button" className="admin-profile-action-btn" onClick={() => {
                                        removeSection(activeSectionIndex);
                                        setActiveSectionIndex((prev) => Math.max(0, Math.min(prev, sections.length - 2)));
                                    }} disabled={sections.length === 1}>-</button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <button type="button" className="admin-profile-action-btn" onClick={addSection}>Add Section</button>
                </div>

                <div className="admin-college-actions">
                    <button
                        type="submit"
                        className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                        disabled={!canCreateTest}
                        title={!canCreateTest ? 'Resolve section validation before creating the test' : 'Create test'}
                    >
                        {submitting ? 'Creating...' : 'Create Test'}
                    </button>
                    <button type="button" className="admin-profile-action-btn" onClick={() => navigate('/admin/submitted-questions?role=admin')} disabled={submitting}>Cancel</button>
                </div>
            </form>
        </div>
    );
};

export default AdminCreateTestPage;
